import type {Asset} from "~/graphql/__generated__/graphql"
import type {Ref} from "vue"
import type AssetLocal from "~/libs/interfaces/AssetLocal"
import {GET_ASSET} from "~/graphql/asset"
import assetLocalFactory from "~/libs/factories/AssetLocalFactory"
import ApolloResponseError from "~/libs/errors/ApolloResponseError"
import NotFoundError from "~/libs/errors/NotFoundError"

export const useAssetsStore = defineStore('assets', {
  state: () => {
    const state: {
      isLoading: Ref<boolean>
      assets: AssetLocal[]
    } = {
      isLoading: ref(false),
      assets: reactive([]),
    }
    return state
  },
  getters: {
    getIsLoading(state): boolean {
      return state.isLoading
    },
  },
  actions: {
    getAsset(id: string): AssetLocal | undefined {
      return this.assets.find((asset) => {
        return asset.id === id
      })
    },
    getAssets(ids: string[]): AssetLocal[] {
      return this.assets.filter((asset) => {
        return asset.id && ids.includes(asset.id)
      })
    },
    async fetchAsset(id: string): Promise<void> {
      const logger = useFrontendLogger()
      const config = useRuntimeConfig()
      const apolloContext = {
        fetchOptions: { signal: AbortSignal.timeout(Number(config.public.assetsStoreTimeout || 5000)) }
      }
      this.isLoading = true
      if (import.meta.server) {
        try {
          const result = await useAsyncQuery(GET_ASSET, {id}, undefined, apolloContext)
          if (result.error?.value) {
            throw new ApolloResponseError(result.error?.value?.message, result.error?.value)
          }

          //@ts-ignore
          const asset = result.data?.value?.asset
          if (!asset) {
            throw new NotFoundError('Asset not found', {id})
          } else {
            this.addAssets([asset], true)
          }
          this.isLoading = false
        } catch (e) {
          logger?.error('useAssetsStore:fetchAsset:server - exception', {
            id,
            errorDetail: e
          })
          this.isLoading = false
          throw e
        }
      } else {
        return new Promise((resolve, reject) => {
          try {
            const options = {
              context: apolloContext
            }
            const {onResult, onError, stop} = useQuery(GET_ASSET, {id}, options)
            onResult((result) => {
              this.isLoading = result.loading
              if (!result.loading) {
                stop()
                const asset = result.data?.asset
                this.addAssets([asset], true)
                resolve()
              }
            })
            onError((error) => {
              this.isLoading = false
              logger?.error('useAssetsStore:fetchAsset:client - error', {
                id,
                errorDetail: error
              })
              reject(new ApolloResponseError(error.message, error))
            })
          } catch (e) {
            this.isLoading = false
            logger?.error('useAssetsStore:fetchAsset:client - exception', {
              id,
              errorDetail: e
            })
            reject(e)
          }
        })
      }
    },
    addAssets(newAssets: Asset[], rewrite: boolean = false): void {
      newAssets.forEach((newAsset) => {
        const assetLocal = assetLocalFactory.createAssetLocal(newAsset)
        const foundIndex = this.assets.findIndex(asset => asset.id === newAsset.id)
        if (foundIndex >= 0 && rewrite) {
          this.assets.splice(foundIndex, 1)
        }
        if (foundIndex === -1 || rewrite) {
          this.assets.push(assetLocal)
        }
      })
    },
  }
})
